import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Autocomplete,
  CircularProgress,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Table,
  TableContainer,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useGetApi } from "../hooks/useGetApi";
import { getUnitApi } from "../services/unit.service";
import {
  addUserApi,
  getRoleApi,
  getUserByIdApi,
  updateUserApi,
  uploadImageApi,
} from "../services/user.service";
import { getDepartmentApi } from "../services/department.service";
import { toast } from "react-toastify";
import {
  DatePicker,
  renderTimeViewClock,
  TimePicker,
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import { Close } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Schema from '../joi/addUser/addUser.schema'; 

const SCHEDULE_SHIFTS_LABEL = [
  { id: "1", label: "Shift", value: "shiftName" },
  { id: "2", label: "Max. Patient", value: "maxPatient" },
  { id: "3", label: "Day", value: "day" },
  { id: "4", label: "From Time", value: "fromTime" },
  { id: "5", label: "To Time", value: "toTime" },
  { id: "6", label: "Amount", value: "amount" },
  { id: "7", label: "", value: "" },
];

const UserAdd = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const user = useSelector((state) => state.user);

  const initialState = {
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    contactNo: "",
    dob: null,
    gender: "",
    identityProof: "",
    identityNo: "",
    unit: null,
    department: null,
    role: null,
    loginThrough: "",
    loginPassword: "",
    profilePicture: "",
    userId: "",
    registrationNumber: "",
    licenseNumber: "",
    regNo: "",
    isIPD: true,
    isOPD: true,
    shifts: [],
  };

  const fileInputRef = useRef(null);
  const fileInputEditRef = useRef(null);

  const [formData, setFormData] = useState(initialState);
  const [scheduleShifts, setScheduleShifts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLoading, setSelectedRowLoading] = useState(false);
  const [selectedRowError, setSelectedRowError] = useState(false);

  // Fetch user data when id changes
  useEffect(() => {
    const fetchUserData = async () => {
      if (!id) {
        setSelectedRow(null);
        return;
      }
      
      setSelectedRowLoading(true);
      setSelectedRowError(false);
      
      try {
        const response = await getUserByIdApi({ id });
        console.log('User API Response:', response);
        
        if (response?.code === 200) {
          setSelectedRow(response.data);
        } else {
          setSelectedRowError(true);
          toast.error(response?.message || "Failed to fetch user data");
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        setSelectedRowError(true);
        toast.error("Failed to fetch user data");
      } finally {
        setSelectedRowLoading(false);
      }
    };

    fetchUserData();
  }, [id]);

  console.log('UserAdd Debug:', { id, selectedRow, selectedRowLoading, selectedRowError });
  console.log(errors, 'errors')

  const { dataList: unitList } = useGetApi({
    apiFunction: getUnitApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
    dataKey: "units",
  });
  const { dataList: roleList } = useGetApi({
    apiFunction: getRoleApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });
  const { dataList: departmentList } = useGetApi({
    apiFunction: getDepartmentApi,
    params: { limit: Number.MAX_SAFE_INTEGER },
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((preValue) => ({
      ...preValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleScheduleShiftChange = (e, index) => {
    const { name, value } = e.target;

    setScheduleShifts((prevShifts) =>
      prevShifts?.map((shift, i) => {
        if (i === index) {
          let newValue = value;

          // Ensure correct data type
          if (name === "fromTime" || name === "toTime") {
            newValue = value ? dayjs(value).format("HH:mm") : null; // Handle null case
          } else if (name === "maxPatient" || name === "amount") {
            newValue = Number(value);
          }

          // Prevent toTime from being before fromTime
          if (name === "toTime" && shift.fromTime && newValue) {
            if (dayjs(newValue).isBefore(dayjs(shift.fromTime))) {
              return shift; // Prevent invalid time update
            }
          }

          return {
            ...shift,
            [name]: newValue,
          };
        }
        return shift;
      })
    );
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleUploadEditClick = () => {
    if (fileInputEditRef.current) {
      fileInputEditRef.current.click();
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const response = await uploadImageApi({
      type: "staff",
      image: file,
    });

    if (response?.code === 200) {
      setFormData((preValue) => ({
        ...preValue,
        profilePicture: response?.data?.Location,
      }));
      toast.success(response?.message || "Image uploaded successfully.");
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
    e.target.value = "";
  };

  // Add table row
  const handleAddTableRow = (currentElement, currentState, newRowData) => {
    currentState([
      ...currentElement,
      { ...newRowData, id: Date.now() }, // Using timestamp for unique temp id
    ]);
  };

  // Remove table row
  const handleRemoveTableRow = (id, currentElement, currentState) => {
    currentState(
      currentElement?.filter((option) => (option?._id || option?.id) !== id)
    );
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    await registerUser();
  };

  const registerUser = async () => {
    let response;

    const formDataWithId = {
      ...formData,
      dob: formData?.dob ? dayjs(formData.dob).format("YYYY-MM-DD") : null,
      department: formData?.department?._id,
      role: formData?.role?._id,
      unit: formData?.unit?._id,
      shifts: scheduleShifts?.map((option) => ({
        ...(selectedRow?._id && option?._id ? { _id: option._id } : {}),
        shiftName: option?.shiftName,
        maxPatient: option?.maxPatient,
        day: option?.day,
        fromTime: option?.fromTime,
        toTime: option?.toTime,
        amount: option?.amount,
      })),
    };

    // If editing and password is empty, remove it from the payload
    if (selectedRow?._id && !formDataWithId.loginPassword) {
      delete formDataWithId.loginPassword;
    }

    console.log(formDataWithId, 'formDataWithId')
    
    // Create conditional schema based on whether we're editing or creating
    const isEditing = selectedRow?._id;
    let validationSchema = Schema;
    
    if (isEditing && !formDataWithId.loginPassword) {
      // If editing and no new password provided, make password optional
      validationSchema = Schema.fork(['loginPassword'], (schema) => schema.optional());
    }
    
    const { error } = validationSchema.validate(formDataWithId, {abortEarly: false});

    if (error) {
      const errors =[];
      error.details.forEach((err) => errors.push(err?.context?.label));
      setErrors(errors);
      toast.error(
        `Please fill all the required fields.`
      );
      return;
    }
    setIsLoading(true);
    if (selectedRow?._id) {
      response = await updateUserApi(formDataWithId);
    } else {
      response = await addUserApi(formDataWithId);
    }
    setIsLoading(false);

    if (response?.code === 200) {
      toast.success(
        response?.message ||
          `User ${selectedRow?._id ? "updated" : "added"} successfully`
      );
      if (selectedRow?._id) {
        navigate(-1);
      } else setFormData(initialState);
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  // Sync selectedRow data when modal opens
  useEffect(() => {
    console.log('useEffect triggered:', { id, selectedRow });
    if (selectedRow?._id) {
      console.log('Setting form data with selectedRow:', selectedRow);
      setFormData({
        ...initialState,
        _id: selectedRow._id,
        title: selectedRow?.title || "",
        firstName: selectedRow?.firstName || "",
        lastName: selectedRow?.lastName || "",
        email: selectedRow?.email || "",
        contactNo: selectedRow?.contactNo || "",
        dob: selectedRow?.dob || null,
        gender: selectedRow?.gender || "",
        identityProof: selectedRow?.identityProof || "",
        identityNo: selectedRow?.identityNo || "",
        unit: selectedRow?.unit || null,
        department: selectedRow?.department || null,
        role: selectedRow?.role || null,
        loginThrough: selectedRow?.loginThrough || "",
        loginPassword: "", // Clear password field for editing - user can optionally enter new password
        profilePicture: selectedRow?.profilePicture || "",
        userId: selectedRow?.userId || "",
        registrationNumber: selectedRow?.registrationNumber || "",
        licenseNumber: selectedRow?.licenseNumber || "",
        regNo: selectedRow?.regNo || "",
        isIPD: selectedRow?.isIPD ?? true,
        isOPD: selectedRow?.isOPD ?? true,
        shifts: selectedRow?.shifts || [],
      });
      setScheduleShifts(selectedRow?.shifts || []);
    } else {
      console.log('Setting initial state');
      setFormData(initialState);
    }
  }, [id, selectedRow]);

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box sx={{}}>
          <Sidebar />
        </Box>
        {id && selectedRowLoading ? (
          <Loader />
        ) : id && selectedRowError ? (
          <ErrorMessage />
        ) : (
          <Box sx={{ width: "100%" }}>
            {/* Form */}

            <Box
              component="form"
              onSubmit={handleFormSubmit}
              sx={{
                padding: 2,
                borderRadius: 2,
                marginTop: 2,
                marginRight: "90px",
              }}
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(-1)}
                sx={{
                  color: '#5D6A82', 
                  textTransform: 'none', 
                  fontSize: '16px',
                  fontWeight: 'bold',
                  padding: 0,
                  transition: 'none !important',
                  mb: 2
                }}
              >
                Back
              </Button>
              {/* User Form */}
              <p
                style={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  marginBottom: "1rem",
                }}
              >
                {`${selectedRow?._id ? "Update" : "Enter"} User Details`}
              </p>

              {/* Personal Information */}
              <Box
                sx={{
                  borderRadius: 2,
                  marginBottom: 2,
                  display: "flex",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: "20rem",
                    display: "flex",
                    flexDirection: "column",
                    align: "center",
                    justifyContent: "space-evenly",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "15rem",
                      border: "1px solid black",
                      borderRadius: 2,
                      marginRight: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      position: "relative",
                    }}
                  >
                    {formData?.profilePicture && (
                      <>
                        <Box
                          component="img"
                          src={formData?.profilePicture}
                          alt="User Profile"
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        ></Box>
                        <Button
                          variant="outlined"
                          sx={{
                            color: "#5d6a82",
                            background: "#dfe8ef",
                            position: "absolute",
                            bottom: 8,
                            right: 8,
                          }}
                          onClick={handleUploadEditClick}
                        >
                          Edit
                        </Button>
                        <input
                          type="file"
                          ref={fileInputEditRef}
                          style={{ display: "none" }}
                          onChange={(e) => handleUpload(e)}
                          inputProps={{ accept: "image/*" }}
                        />
                      </>
                    )}
                  </Box>
                  {!formData?.profilePicture && (
                    <>
                      <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={(e) => handleUpload(e)}
                        inputProps={{ accept: "image/*" }}
                      />
                      <Button
                        variant="outlined"
                        sx={{
                          borderRadius: 2,
                          width: "100%",
                          height: "3rem",
                          color: "#0179B0",
                          textTransform: "none",
                        }}
                        onClick={handleUploadClick}
                      >
                        Upload
                      </Button>
                    </>
                  )}
                </Box>

                <Box
                  sx={{
                    backgroundColor: "#f5faff",
                    padding: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle1" marginBottom={2} color="grey">
                    Personal Information
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <Box sx={{ display: "flex", gap: 1, flex: "1 1 Calc(50% - 8px)" }}>
                      <FormControl sx={{ minWidth: 80 }}>
                        <InputLabel
                          sx={{ backgroundColor: "#f5faff", padding: "0px 4px" }}
                        >
                          Title
                        </InputLabel>
                        <Select
                          value={formData?.title || ""}
                          onChange={handleInputChange}
                          name="title"
                          size="medium"
                        >
                          <MenuItem value="">None</MenuItem>
                          <MenuItem value="Mr.">Mr.</MenuItem>
                          <MenuItem value="Mrs.">Mrs.</MenuItem>
                          <MenuItem value="Dr.">Dr.</MenuItem>
                          <MenuItem value="Ms.">Ms.</MenuItem>
                          <MenuItem value="Prof.">Prof.</MenuItem>
                          <MenuItem value="Er.">Er.</MenuItem>
                          <MenuItem value="Sister">Sister</MenuItem>
                          <MenuItem value="Rev.">Rev.</MenuItem>
                          <MenuItem value="Capt.">Capt.</MenuItem>
                          <MenuItem value="Col.">Col.</MenuItem>
                          <MenuItem value="Maj.">Maj.</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        label="First Name"
                        value={formData?.firstName}
                        onChange={handleInputChange}
                        required
                        InputLabelProps={{
                          required: true,
                          sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              border: errors?.includes('firstName') ? '1px solid red' : '1px solid inherit',
                            },
                          },
                          flex: 1
                        }}
                        name="firstName"
                      />
                    </Box>
                    <TextField
                      label="Last Name"
                      // sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('lastName') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                        flex: "1 1 Calc(50% - 8px)"
                      }}
                      value={formData?.lastName}
                      onChange={handleInputChange}
                      name="lastName"
                    />
                    <TextField
                      label="Email"
                      type="email"
                      // sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('email') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                        flex: "1 1 Calc(50% - 8px)"
                      }}
                      value={formData?.email}
                      onChange={handleInputChange}
                      name="email"
                    />
                    <TextField
                      label="Contact Number"
                      // sx={{ flex: "1 1 Calc(50% - 8px)" }}
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('contactNo') ? '1px solid red' : '1px solid inherit',
                          },
                        },
                        flex: "1 1 Calc(50% - 8px)"
                      }}
                      value={formData?.contactNo}
                      onChange={handleInputChange}
                      name="contactNo"
                    />
                    <DatePicker
                      label="DOB"
                      disableFuture
                      maxDate={dayjs()}
                      slotProps={{ textField: { fullWidth: true } }}
                      value={formData?.dob ? dayjs(formData.dob) : null}
                      onChange={(newValue) =>
                        handleInputChange({
                          target: { name: "dob", value: newValue },
                        })
                      }
                      sx={{ flex: "1 1 Calc(50% - 8px)" }}
                    />
                    <FormControl sx={{ flex: "1 1 Calc(50% - 8px)" }}>
                      <InputLabel
                        required
                        sx={{ 
                          backgroundColor: "#f5faff", 
                          padding: "0px 4px",
                          '& .MuiFormLabel-asterisk': { color: 'red' }
                        }}
                      >
                        Gender
                      </InputLabel>
                      <Select
                        value={formData?.gender}
                        onChange={handleInputChange}
                        sx={{border: errors?.includes('gender') ? '1px solid red' : '1px solid inherit',}}
                        name="gender"
                      >
                        <MenuItem value="male">Male</MenuItem>
                        <MenuItem value="female">Female</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: "1 1 Calc(50% - 8px)" }}>
                      <InputLabel
                        required
                        sx={{ 
                          backgroundColor: "#f5faff", 
                          padding: "0px 4px",
                          '& .MuiFormLabel-asterisk': { color: 'red' }
                        }}
                      >
                        Select Identification Proof
                      </InputLabel>
                      <Select
                        value={formData?.identityProof}
                        onChange={handleInputChange}
                        name="identityProof"
                        sx={{border: errors?.includes('identityProof') ? '1px solid red' : '1px solid inherit',}}
                      >
                        <MenuItem value="aadhar">Aadhar</MenuItem>
                        <MenuItem value="pan">Pan</MenuItem>
                        <MenuItem value="passport">Passport</MenuItem>
                        <MenuItem value="drivingLicense">
                          Driving License
                        </MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      label={
                        {
                          aadhar: "Aadhar Number",
                          pan: "PAN Number",
                          passport: "Passport Number",
                          drivingLicense: "Driving License Number",
                        }[formData?.identityProof] || "Identification Number"
                      }
                      required
                      InputLabelProps={{
                        required: true,
                        sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                      }}
                      sx={{ flex: "1 1 Calc(50% - 8px)",
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            border: errors?.includes('identityNo') ? '1px solid red' : '1px solid inherit',
                          },
                        }
                       }}
                      value={formData?.identityNo}
                      onChange={handleInputChange}
                      name="identityNo"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Professional Information */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" color="grey" marginBottom={2}>
                  Professional Information
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <FormControl sx={{ flex: "1 1 30%" }}>
                    <Autocomplete
                      options={unitList || []}
                      getOptionLabel={(option) => option?.unitName || ""}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Unit"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('unit') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                      value={formData?.unit || ""}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "unit", value: newValue },
                        })
                      }
                      disabled={selectedRow?._id}
                    />
                  </FormControl>

                  <FormControl sx={{ flex: "1 1 30%" }}>
                    <Autocomplete
                      options={departmentList || []}
                      getOptionLabel={(option) => option?.name || ""}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Department"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('department') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                      value={formData?.department || ""}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "department", value: newValue },
                        })
                      }
                      disabled={selectedRow?._id}
                    />
                  </FormControl>

                  <FormControl sx={{ flex: "1 1 30%" }}>
                    <Autocomplete
                      options={roleList?.filter((role) => {
                        // Super Admin can see all roles
                        if (user.role === 'Super Admin') {
                          return true;
                        }
                        // Admin can see all roles except Super Admin
                        if (user.role === 'Admin') {
                          return role.role !== 'Super Admin';
                        }
                        // Other roles shouldn't be creating users, but if they are, exclude Admin and Super Admin
                        return role.role !== 'Admin' && role.role !== 'Super Admin';
                      }) || []}
                      getOptionLabel={(option) => option?.role || ""}
                      renderInput={(params) => (
                        <TextField 
                          {...params} 
                          label="Role"
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                border: errors?.includes('role') ? '1px solid red' : '1px solid inherit',
                              },
                            },
                          }} 
                        />
                      )}
                      value={formData?.role || ""}
                      onChange={(_, newValue) =>
                        handleInputChange({
                          target: { name: "role", value: newValue },
                        })
                      }
                      disabled={selectedRow?._id}
                    />
                  </FormControl>
                </Box>

                {/* Doctor Registration Fields - Only show for doctors */}
                {formData?.role?.role === "Doctor" && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, marginTop: 2 }}>
                    <TextField
                      label="Registration Number"
                      value={formData?.registrationNumber || ""}
                      onChange={handleInputChange}
                      name="registrationNumber"
                      sx={{ flex: "1 1 30%" }}
                      placeholder="Enter doctor registration number"
                    />
                    <TextField
                      label="License Number"
                      value={formData?.licenseNumber || ""}
                      onChange={handleInputChange}
                      name="licenseNumber"
                      sx={{ flex: "1 1 30%" }}
                      placeholder="Enter license number"
                    />
                    <TextField
                      label="Registration ID"
                      value={formData?.regNo || ""}
                      onChange={handleInputChange}
                      name="regNo"
                      sx={{ flex: "1 1 30%" }}
                      placeholder="Enter registration ID"
                    />
                  </Box>
                )}
              </Box>

              {/* Schedule Shifts*/}
              {formData?.role?.role === "Doctor" && (
                <Box
                  sx={{
                    border: `1px solid #CCD4DA`,
                    borderRadius: "12px",
                    mb: "30px",
                    p: "20px",
                    fontSize: "14px",
                    color: "#5D6A82",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography sx={{ color: "inherit", fontSize: "14px" }}>
                      Schedule Shifts
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isIPD"
                            checked={formData?.isIPD || false}
                            onChange={handleInputChange}
                          />
                        }
                        label="IPD"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="isOPD"
                            checked={formData?.isOPD || false}
                            onChange={handleInputChange}
                          />
                        }
                        label="OPD"
                      />
                    </Box>
                  </Box>

                  {/* Table */}

                  <TableContainer
                    sx={{ border: "1px solid #CCD4DA", borderRadius: "12px" }}
                  >
                    <Table
                      aria-label="inhouse medicines table"
                      sx={{ minWidth: 800 }}
                    >
                      <TableHead
                        sx={{
                          bgcolor: "#EAEAEA",
                        }}
                      >
                        <TableRow>
                          {SCHEDULE_SHIFTS_LABEL?.map((option) => (
                            <TableCell
                              key={option?.id}
                              sx={{
                                textAlign: option?.align || "left",
                                color: "#5D6A82",
                              }}
                            >
                              {option?.label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {scheduleShifts?.map((row, index) => (
                          <TableRow key={row?._id || row?.id}>
                            <TableCell sx={{ color: "#5D6A82" }}>
                              <FormControl fullWidth>
                                <InputLabel>Shift</InputLabel>
                                <Select
                                  label="Shift"
                                  name="shiftName"
                                  value={row?.shiftName}
                                  onChange={(e) =>
                                    handleScheduleShiftChange(e, index)
                                  }
                                >
                                  <MenuItem value="All Day">All Day</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ color: "#5D6A82" }}>
                              <TextField
                                type="number"
                                label="Max. Patient"
                                name="maxPatient"
                                value={row?.maxPatient || ""}
                                onChange={(e) =>
                                  handleScheduleShiftChange(e, index)
                                }
                              />
                            </TableCell>

                            <TableCell sx={{ color: "#5D6A82" }}>
                              <FormControl fullWidth>
                                <InputLabel>Day</InputLabel>
                                <Select
                                  label="Day"
                                  name="day"
                                  value={row?.day}
                                  onChange={(e) =>
                                    handleScheduleShiftChange(e, index)
                                  }
                                >
                                  <MenuItem value="Monday">Monday</MenuItem>
                                  <MenuItem value="Tuesday">Tuesday</MenuItem>
                                  <MenuItem value="Wednesday">
                                    Wednesday
                                  </MenuItem>
                                  <MenuItem value="Thursday">Thursday</MenuItem>
                                  <MenuItem value="Friday">Friday</MenuItem>
                                  <MenuItem value="Saturday">Saturday</MenuItem>
                                  <MenuItem value="Sunday">Sunday</MenuItem>
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ color: "#5D6A82" }}>
                              <TimePicker
                                ampm={false}
                                label="From Time"
                                value={
                                  row?.fromTime
                                    ? dayjs(row?.fromTime, "HH:mm")
                                    : null
                                }
                                onChange={(newValue) => {
                                  handleScheduleShiftChange(
                                    {
                                      target: {
                                        name: "fromTime",
                                        value: newValue,
                                      },
                                    },
                                    index
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField {...params} />
                                )}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#5D6A82" }}>
                              <TimePicker
                                label="To Time"
                                value={
                                  row?.toTime
                                    ? dayjs(row?.toTime, "HH:mm")
                                    : null
                                }
                                onChange={(newValue) => {
                                  handleScheduleShiftChange(
                                    {
                                      target: {
                                        name: "toTime",
                                        value: newValue,
                                      },
                                    },
                                    index
                                  );
                                }}
                                viewRenderers={{
                                  hours: renderTimeViewClock,
                                  minutes: renderTimeViewClock,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ color: "#5D6A82" }}>
                              <TextField
                                type="number"
                                label="Amount"
                                name="amount"
                                value={row?.amount || ""}
                                onChange={(e) =>
                                  handleScheduleShiftChange(e, index)
                                }
                              />
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: "#5D6A82",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Close
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "18px",
                                  }}
                                  onClick={() =>
                                    handleRemoveTableRow(
                                      row?._id || row?.id,
                                      scheduleShifts,
                                      setScheduleShifts
                                    )
                                  }
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            colSpan={2}
                            sx={{ color: "#92A6CC", cursor: "pointer" }}
                            onClick={() =>
                              handleAddTableRow(
                                scheduleShifts,
                                setScheduleShifts,
                                {}
                              )
                            }
                          >
                            + Add item
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Login Credentials */}
              <Box
                sx={{
                  padding: 2,
                  backgroundColor: "#f5faff",
                  borderRadius: 2,
                  marginBottom: 2,
                }}
              >
                <Typography variant="subtitle1" color="grey" marginBottom={2}>
                  Create Login Credentials
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  <TextField
                    label="User ID"
                    value={formData?.userId || ""}
                    onChange={handleInputChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    name="userId"
                    sx={{
                      flex: "1 1 30%",
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes('userId') ? '1px solid red' : '1px solid inherit',
                        },
                      },
                    }} 
                  />
                  <TextField
                    label="Email/Mobile Number"
                    value={formData?.loginThrough || ""}
                    onChange={handleInputChange}
                    required
                    InputLabelProps={{
                      required: true,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    name="loginThrough"
                    sx={{
                      flex: "1 1 30%",
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes('loginThrough') ? '1px solid red' : '1px solid inherit',
                        },
                      },
                    }} 
                  />

                  <TextField
                    label={selectedRow?._id ? "New Password (Optional)" : "Password"}
                    placeholder={selectedRow?._id ? "Leave empty to keep current password" : "Enter password"}
                    type="password"
                    required={!selectedRow?._id}
                    InputLabelProps={{
                      required: !selectedRow?._id,
                      sx: { '& .MuiFormLabel-asterisk': { color: 'red' } }
                    }}
                    value={formData?.loginPassword || ""}
                    onChange={handleInputChange}
                    name="loginPassword"
                    sx={{
                      flex: "1 1 30%",
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          border: errors?.includes('loginPassword') ? '1px solid red' : '1px solid inherit',
                        },
                      },
                    }} 
                  />
                </Box>
              </Box>

              {/* Register Button */}
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  sx={{ padding: "10px 40px", textTransform: "none" }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <CircularProgress size={24} />
                  ) : selectedRow?._id ? (
                    "Update"
                  ) : (
                    `Register`
                  )}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default UserAdd;
